import React, { useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Alert,
  AlertIcon,
  Box,
  Button,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { TbWand } from "react-icons/tb";
import { autoAssignGroupsToTrades } from "../../api/Trades";
import { TradeEx } from "../../types";

const FIELD_LABELS: Record<string, string> = {
  POSITION_GROUP: "position group",
  STRATEGY: "strategy",
  BOOK: "book",
};

interface AssignStrategyButtonProps {
  trades: TradeEx[];
  accountId: number;
  onComplete: () => void;
}

export const AssignStrategyButton = ({
  trades,
  accountId,
  onComplete,
}: AssignStrategyButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [field, setField] = useState<string>("POSITION_GROUP");
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleConfirm = async () => {
    if (!accountId || trades.length === 0) {
      toast({
        title: "No trades available",
        description: "There are no trades to assign strategies to.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      const tradeIds = trades.map((trade) => trade.id).filter((id): id is number => id !== undefined);
      
      const result = await autoAssignGroupsToTrades(tradeIds, accountId, field);
      
      if (result?.success) {
        toast({
          title: "Strategies assigned",
          description: `Successfully assigned strategies to ${FIELD_LABELS[field]} field.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onComplete();
      } else {
        throw new Error(result?.message || "Failed to assign strategies");
      }
    } catch (error) {
      toast({
        title: "Error assigning strategies",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <>
      <Button
        leftIcon={<TbWand size={16} />}
        size="sm"
        variant="outline"
        onClick={onOpen}
        colorScheme="blue"
        borderColor="var(--accent)"
        color="var(--accent)"
        _hover={{ 
          bg: "var(--accent)", 
          color: "var(--white)",
        }}
        fontWeight="medium"
      >
        Auto-Assign
      </Button>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="var(--bg)">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Auto-Assign Strategies
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4} color="var(--light-gray)">
                Automatically detect and assign option strategies (e.g., Iron Condor, 
                Bull Call Spread, Long Call) to your trades based on their characteristics.
              </Text>
              
              <Text mb={2} fontWeight="medium">
                Assign to field:
              </Text>
              
              <RadioGroup value={field} onChange={setField}>
                <Stack spacing={3}>
                  <Radio value="POSITION_GROUP" colorScheme="blue">
                    <Text>Position Group</Text>
                    <Text fontSize="sm" color="var(--light-gray)">
                      Updates the position grouping field
                    </Text>
                  </Radio>
                  <Radio value="STRATEGY" colorScheme="blue">
                    <Text>Strategy</Text>
                    <Text fontSize="sm" color="var(--light-gray)">
                      Updates the strategy field
                    </Text>
                  </Radio>
                  <Radio value="BOOK" colorScheme="blue">
                    <Text>Book</Text>
                    <Text fontSize="sm" color="var(--light-gray)">
                      Updates the book field
                    </Text>
                  </Radio>
                </Stack>
              </RadioGroup>

              <Alert status="warning" mt={4} borderRadius="md" bg="var(--bg-hover)">
                <AlertIcon color="orange.400" />
                <Text fontSize="sm">
                  This will replace any pre-existing values in the selected field.
                </Text>
              </Alert>

              <Text mt={4} fontSize="sm" color="var(--light-gray)">
                This will analyze {trades.length} trade{trades.length !== 1 ? "s" : ""} and 
                assign appropriate strategies.
              </Text>

              <Box mt={4} p={3} bg="var(--bg-hover)" borderRadius="md">
                <Text fontSize="sm" color="var(--light-gray)">
                  <Text as="span" fontWeight="medium" color="var(--white)">Tip:</Text>{" "}
                  For targeted assignment, right-click on a group or select specific trades, 
                  then go to <Text as="span" fontWeight="medium">Advanced → Assign Strategy</Text>.
                </Text>
              </Box>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleConfirm}
                ml={3}
                isLoading={isLoading}
                loadingText="Assigning..."
              >
                Assign Strategies
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
